import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarBlank, CheckCircle, WarningCircle } from '@phosphor-icons/react';

import {
  approveCommand,
  dismissCommand,
  listCommands,
  preflightCommand,
  type CommandIntent,
} from '@/api/commands';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';

// ProposalsPanel — pending schedule proposals a leader sent from the app
// (plan W4b). Admins review them here on the Assistant page: each shows a
// preflight conflict verdict before the approve button, so a double-booking is
// visible up front. Approve runs the existing CommandIntent approve (the
// authoritative gate); reject dismisses. Renders nothing when there are none,
// so it stays out of the way on a quiet day.
//
// We surface only pending, user-originated intents — AI proposals live in the
// command bar, and pending intents carry a 15-min TTL so the recent page covers
// them without a dedicated status filter on the list endpoint.
export function ProposalsPanel({ slug, token }: { slug: string; token: string | null }) {
  const { t } = useTranslation('common');
  const list = useQuery({
    queryKey: ['commands', slug, 'pending-proposals'],
    queryFn: () => listCommands(token, slug, { limit: 50 }),
    enabled: !!token && !!slug,
    // Pending intents expire on a 15-min TTL; refetch so a stale one drops off.
    refetchInterval: 60_000,
  });

  const proposals = (list.data?.items ?? []).filter(
    (i) => i.status === 'pending' && i.actor_kind === 'user',
  );
  if (proposals.length === 0) return null;

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-xs md:p-5">
      <div className="flex items-center gap-2">
        <CalendarBlank weight="duotone" className="size-5 text-foreground/70" />
        <h2 className="font-heading text-lg font-bold tracking-tight">
          {t('proposals.title')}
        </h2>
        <span className="rounded-pill bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground tabular-nums">
          {proposals.length}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{t('proposals.intro')}</p>
      <ul className="flex flex-col gap-3">
        {proposals.map((p) => (
          <ProposalRow key={p.id} slug={slug} token={token} intent={p} t={t} />
        ))}
      </ul>
    </section>
  );
}

function ProposalRow({
  slug,
  token,
  intent,
  t,
}: {
  slug: string;
  token: string | null;
  intent: CommandIntent;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const verdict = useQuery({
    queryKey: ['preflight', slug, intent.id],
    queryFn: () => preflightCommand(token, slug, intent.id),
    enabled: !!token && !!slug,
  });
  const blocked = verdict.data?.blocked ?? false;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['commands', slug, 'pending-proposals'] });
  };

  const approve = useMutation({
    mutationFn: () => approveCommand(token, slug, intent.id),
    onSuccess: () => {
      toast.success(t('proposals.approved'));
      refresh();
    },
    onError: () => toast.error(t('proposals.actionFailed')),
  });
  const reject = useMutation({
    mutationFn: () => dismissCommand(token, slug, intent.id),
    onSuccess: () => {
      toast.success(t('proposals.rejected'));
      refresh();
    },
    onError: () => toast.error(t('proposals.actionFailed')),
  });

  const onApprove = async () => {
    // A blocked proposal would (likely) fail at execute anyway — make the admin
    // confirm before approving over a known conflict.
    if (blocked) {
      const ok = await confirm({
        title: t('proposals.approveBlocked.title'),
        body: t('proposals.approveBlocked.body'),
        confirmLabel: t('proposals.approve'),
        tone: 'destructive',
      });
      if (!ok) return;
    }
    approve.mutate();
  };

  const busy = approve.isPending || reject.isPending;

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {intent.plan_summary || t('proposals.untitled')}
          </p>
          {intent.actor_name || intent.actor_email ? (
            <p className="truncate text-xs text-muted-foreground">
              {t('proposals.from', { name: intent.actor_name || intent.actor_email })}
            </p>
          ) : null}
        </div>
        <Verdict loading={verdict.isLoading} blocked={blocked} detail={verdict.data?.checks} t={t} />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => reject.mutate()} disabled={busy}>
          {t('proposals.reject')}
        </Button>
        <Button size="sm" onClick={onApprove} disabled={busy}>
          {t('proposals.approve')}
        </Button>
      </div>
    </li>
  );
}

function Verdict({
  loading,
  blocked,
  detail,
  t,
}: {
  loading: boolean;
  blocked: boolean;
  detail: { status: string }[] | undefined;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  if (loading) {
    return <span className="shrink-0 text-xs text-muted-foreground">{t('proposals.verdict.checking')}</span>;
  }
  if (blocked) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">
        <WarningCircle weight="fill" className="size-3.5" />
        {t('proposals.verdict.blocked')}
      </span>
    );
  }
  // Only show the green "clean" pill once we actually have a verdict.
  if (!detail) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-primary/10 px-2 py-0.5 text-xs font-semibold text-foreground">
      <CheckCircle weight="fill" className="size-3.5 text-primary" />
      {t('proposals.verdict.clean')}
    </span>
  );
}
